import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Moment from 'moment';
import {
  AppFileUploader,
  AppRemixIcon,
  AppTextArea,
  AppModal,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import AppInput from '../../../../../components/form/AppInput';
import colors from '../../../../../../constants/colors';
import minions from '../../../../../../minions/minions';
import Translator from '../../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../../routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../components/hooks/useIsMounted';

export default function MassiveEvidenceEditForm({
  isLoading,
  onSave,
  elementID,
}) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const view = useSelector((state) => state.currentPage?.view);
  const resLevel1 = useSelector(
    (state) => state.currentPage?.resourceObligationLevels?.level1
  );
  const checkedResources = useSelector(
    (state) => state.currentPage?.checkedResources?.[elementID]
  );
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState();
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      ReactDOMServer.renderToString(
        <Translator file="user" name="user.required" />
      )
    ),
  });

  const handleUpload = (values, setFieldValue, opt) => {
    const files = Array.from(opt).filter((f) => !values.files.includes(f.name));
    setFieldValue('files', [...values.files, ...files]);
  };

  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_resourceManager_resourceObligation_massive_prevision_evidence_set"
          ),
          minions.forms.toFormData({
            ...values,
            moduleBridgeHex,
            resourceObligationIDs: checkedResources,
            view,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          if (isMounted.current) {
            setError(undefined);
            dispatch(currentPageAction.change("updateCard", elementID, true));
            dispatch(
              currentPageAction.change(
                "updateResourceObligationTab",
                elementID,
                true
              )
            );
            setShowModal(false);
            onSave();
          }
        })
        .catch((e) => {
          if (e.response.status === 406) {
            setError(e.response.data);
          } else {
            dispatch(errorHandler(e));
          }
        }),
    [moduleBridgeHex, checkedResources, resLevel1, view]
  );

  return (
    <>
      <button
        type="button"
        className="btn btn-xs btn-success"
        onClick={() => setShowModal(true)}
        disabled={!checkedResources || checkedResources?.length === 0}
      >
        <AppRemixIcon icon="attachment-2" className="mr-1" noVariants />
        <Translator
          file="module-resourceManager"
          name="resource.associate.massive.evidence"
        />
      </button>
      <AppModal
        title={
          <Translator
            file="module-resourceManager"
            name="resource.associate.massive.evidence"
          />
        }
        show={showModal}
        onClose={() => setShowModal(false)}
        size="lg"
      >
        <Formik
          initialValues={{
            name: '',
            description: '',
            files: [],
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSave}
        >
          {({ handleSubmit, values, setFieldValue, errors }) => (
            <form onSubmit={handleSubmit}>
              <div className="container-fluid">
                {error && (
                  <div className="alert alert-danger mt-2 mb-2">{error}</div>
                )}
                <div className="row">
                  <div className="col-6">
                    <AppInput
                      title={
                        <Translator file="module-legalCompliance" name="name" />
                      }
                      placeholder={
                        <Translator
                          file="module-legalCompliance"
                          name="name.description.compulsory"
                        />
                      }
                      icon={<AppRemixIcon icon="double-quotes-l" noVariants />}
                      value={values.name}
                      error={errors.name}
                      onChange={(opt) => setFieldValue('name', opt)}
                      isClearable
                    />
                    <AppInput
                      title={
                        <Translator file="module-legalCompliance" name="date" />
                      }
                      icon={<AppRemixIcon icon="calendar" />}
                      value={Moment().format('DD/MM/YYYY')}
                      isReadOnly
                    />
                    <AppTextArea
                      title={
                        <Translator
                          file="module-legalCompliance"
                          name="module.lc.evidence.description"
                        />
                      }
                      placeholder={
                        <Translator
                          file="module-legalCompliance"
                          name="name.description.compulsory"
                        />
                      }
                      icon={<AppRemixIcon icon="align-left" noVariants />}
                      onChange={(opt) => setFieldValue('description', opt)}
                      value={values.description}
                    />
                  </div>
                  <div className="col-6">
                    <div
                      className="row"
                      style={{
                        overflowY: 'scroll',
                        maxHeight: '113px',
                      }}
                    >
                      <div className="col-lg-12">
                        <EvidenceFileList urls={false}>
                          {values?.files.map((f) => (
                            <li
                              key={f}
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="click.delete"
                                />
                              )}
                              onClick={() =>
                                setFieldValue(
                                  'files',
                                  values.files.filter((file) => file !== f)
                                )
                              }
                            >
                              <AppRemixIcon
                                className="pointer mr-3"
                                icon="delete-bin"
                                color={colors.danger}
                              />
                              {minions.files.fileIcon(
                                f.name,
                                true,
                                null,
                                'mr-2'
                              )}
                              {f?.name}
                            </li>
                          ))}
                        </EvidenceFileList>
                      </div>
                    </div>
                    <AppFileUploader
                      isMulti
                      containerClassName="mt-0"
                      placeholder={
                        values.files.length === 0 ? (
                          <Translator
                            file="module-legalCompliance"
                            name="select.one.multiple.files"
                          />
                        ) : (
                          <Translator
                            file="general"
                            name="select.number.of.elements"
                            variables={{
                              elements: values.files.length,
                            }}
                          />
                        )
                      }
                      onChange={(opt) =>
                        handleUpload(values, setFieldValue, opt)
                      }
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>
                </div>
                <div className="my-2">
                  <span
                    className={`btn btn-xs btn-outline-light mr-2 ${
                      isLoading ? 'disabled' : ''
                    }`}
                    onClick={() => setShowModal(false)}
                  >
                    <Translator file="user" name="user.cancel" />
                  </span>
                  <div className="float-right">
                    <button
                      type="button"
                      className={`btn btn-xs btn-warning ${
                        isLoading ? 'disabled' : ''
                      } mr-2`}
                      onClick={() => {
                        setFieldValue('state', 2);
                        handleSubmit();
                      }}
                    >
                      <Translator
                        file="module-resourceManager"
                        name="resource.state2"
                      />
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs btn-success ${
                        isLoading ? 'disabled' : ''
                      }`}
                      onClick={() => {
                        setFieldValue('state', 1);
                        handleSubmit();
                      }}
                    >
                      <Translator
                        file="module-resourceManager"
                        name="resource.state1"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
}

const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.urls ? colors.grey200 : 'initial')};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
